import type React from 'react';

import {
  LikeIcon,
  LikeIconActiveWeb,
  SameIcon,
  SameIconActiveWeb,
  UnLikeIcon,
  UnLikeIconActiveWeb,
} from '@whelk/assets/src/web/icons';
import type { CiCallResult } from '@whelk/const/src/markdown';
import type { GenerateImage, StylizedReplyInfo } from '@whelk/services/src/share';
import {
  type ReplyStyle,
  type SearchLinks,
  type FormItemTypeEnum,
  type MultiModalFormItem,
  type ChatModalStyleEnum,
  type FeedbackInfo,
  type FollowUpQuestion,
  type UrlStatus,
  type ChatMsgTypeEnum,
  REQUEST_STATUS,
} from '@whelk/services/src/type/chat';

export const DefaultSystemId = '0';

export const SYSTEM_CARD_TYPE = {
  VOICE_RECOGNITION: 'voiceRecognition',

  // 初始状态
  LOADING: 'normalLoading', // 此时 requestStatus 返回为 0 没有内容时候loading 展示

  // 联网搜索状态
  SEARCHING: 'searchLoading', // 此时 正在联网搜索中

  // 绘制状态
  PAINTING: 'paintLoading', // 此时正在绘图中
  PAINT_SUCCESS: 'paintSuccess', // 此时正在绘图中

  // 蹦字时状态
  JUMPING: 'jumping', // 蹦字loading
  JUMPING_WAITING: 'jumpingWaiting', // 没有字可蹦了，此时在等待后端返回后续文字

  // 最终状态
  RE_PULL: 'rePull', // 重拉
  SHOW_CONTINUE: 'showContinue', // 完成接口请求：蹦字结束之后 展示继续生成按钮
  OTHER_FAIL: 'otherFail', // 其他失败：超时,其他异常等
  CONTINUE_FAIL: 'CONTINUE_FAIL', // 后续回复失败等 requeststate 为12 不算 敏感词失败，因为可以重试
  SENSITIVE_FAIL: 'sensitiveFail', // 敏感词失败  requeststate 为 4 一开始直接敏感词失败，不可以重试，重试结果 还是会失败
  SUCCESS: 'success', // 完成接口请求：蹦字结束之后 展示正常文案
  READING: 'reading', // 搜索完成后阅读中
  FINISH: 'finish', // 搜索完成后完成
};

export const JUMP_STATUS = [SYSTEM_CARD_TYPE.JUMPING, SYSTEM_CARD_TYPE.JUMPING_WAITING];

export const NEED_CARD_FULL = [
  SYSTEM_CARD_TYPE.SEARCHING,
  SYSTEM_CARD_TYPE.PAINTING,
  SYSTEM_CARD_TYPE.PAINT_SUCCESS,
];

// 失败状态
export const FAIL_STATUS = [
  SYSTEM_CARD_TYPE.OTHER_FAIL,
  SYSTEM_CARD_TYPE.CONTINUE_FAIL,
  SYSTEM_CARD_TYPE.SENSITIVE_FAIL,
];

// requeststate 转 cardtype
export const REQUEST_STATE_TO_CARD_TYPE_MAP = {
  [REQUEST_STATUS.SENSITIVE]: SYSTEM_CARD_TYPE.SENSITIVE_FAIL,
  [REQUEST_STATUS.CONTINUESENSITIVE]: SYSTEM_CARD_TYPE.SENSITIVE_FAIL,
  [REQUEST_STATUS.OTHER_ERROR]: SYSTEM_CARD_TYPE.OTHER_FAIL,
  [REQUEST_STATUS.TIMEOUT]: SYSTEM_CARD_TYPE.OTHER_FAIL,
};

export const FINISH_STATUS = [
  SYSTEM_CARD_TYPE.SHOW_CONTINUE,
  SYSTEM_CARD_TYPE.OTHER_FAIL,
  SYSTEM_CARD_TYPE.SENSITIVE_FAIL,
  SYSTEM_CARD_TYPE.CONTINUE_FAIL,
  SYSTEM_CARD_TYPE.SUCCESS,
  SYSTEM_CARD_TYPE.RE_PULL,
];

export type SystemCardUITypeEnum = (typeof SYSTEM_CARD_TYPE)[keyof typeof SYSTEM_CARD_TYPE];

export const enum FEADBACKTYPE {
  CANCEL_DISLIKE = 0,
  DISLIKE = 2,
}

export interface SystemListItem {
  cardType: SystemCardUITypeEnum;
  requestContent: string; // 拼接的markdown内容
  systemMsgID: string; // 对话列表id 唯一键值
  failingText?: string; // systemCardUITypeEnum 未 failing 状态的文案
  chatSearchInfo: ChatSearchInfoMeta; // 联网搜索数据
  feedbackPopUp?: FeedbackInfo;
  followUpQuestions?: FollowUpQuestion[];
  sugTip?: string;
  generateImage?: GenerateImage;
  feadbackType?: FEADBACKTYPE;
  msgSubtype?: ChatMsgTypeEnum;
  stylizedReplyList?: StylizedReplyInfo[];
  isUserStopped?: boolean;
  ciCallResults?: CiCallResult[];
  warningText?: string;
  thinkTime?: number;
}

export interface SystemCardProps {
  requestContent: string; // 拼接的markdown内容
  msgId: string; // 对话id
  parentMsgID?: string; // user id
  generateImage?: GenerateImage;
  netSearchList?: SearchLinks[];
  thumbnail?: {
    prefix: string;
    desc: {
      text: string;
      icon?: string;
    }[];
  };
  stylizedReplyList?: StylizedReplyInfo[];
  renderFailTextCmp?: () => JSX.Element | null;
  renderLastFailTextCmp?: () => JSX.Element | null;
}

export interface ChatSearchInfoMeta {
  netSearchList: SearchLinks[]; // 联网搜索链接
  status?: string;
  statusCode: ChatModalStyleEnum;
  thumbnail?: {
    prefix: string;
    desc: {
      text: string;
      icon?: string;
    }[];
  };
}

export interface ChatListUserItem {
  longCutCopyWriting?: string;
  msgType: 'user';
  msgContent: string; // 返回内容
  msgId: string;
  multiModalForm: MultiModalFormItem[];
  msgSubtype?: number;
  urlStatus?: UrlStatus;
  warningText?: string;
}

export interface ChatListSystemItem {
  msgType: 'system';
  msgId: string; // 当前的 msgId
  parentMsgID?: string;
  finishStatus: SystemCardUITypeEnum | null; // 接口请求先于蹦字处理（接口js逻辑和UI蹦字逻辑不是一起处理所以需要在接口处），蹦字完了之后需要将状态置为对应的接口状态.  属于暂存数据
  targetSystemListIndex: number; // 重试多次列表 当前展示的索引
  systemList: Array<SystemListItem>; // 系统返回列表
  isSugSystemItem?: boolean; // 是否是sug卡片
}

export interface MultiModalComponentData {
  currentFileType: FormItemTypeEnum;
  fileUrl?: string;
  fileName: string;
  realFile?: File;
  file?: File;
  fileID?: string;
  file_size?: number;
  file_type?: string;
  uuid?: string;
  ossPath?: string;
  height?: number;
  width?: number;
  upload_type?: 'click' | 'drag' | 'paste';
}

export const NATIVE_VOICE_DEFAULT_TEXT = '正在发送语音消息';

export const DEFAULT_SYSTEM_ID = '0';

type FeedbackTemplateListType = {
  [key: number]: {
    icon: () => React.ReactElement;
    activeIcon: () => React.ReactElement;
    title?: string;
    action: number;
    score: string;
  }[];
};

export const FEEDBACK_TEMPLATE_LIST: FeedbackTemplateListType = {
  2: [
    {
      title: 'dialog_feedback_better',
      action: 1,
      score: 'better',
      icon: LikeIcon,
      activeIcon: LikeIconActiveWeb,
    },
    {
      title: 'dialog_feedback_worse',
      action: 2,
      score: 'worse',
      icon: UnLikeIcon,
      activeIcon: UnLikeIconActiveWeb,
    },
    {
      title: 'dialog_feedback_same',
      score: 'equal',
      action: 3,
      icon: SameIcon,
      activeIcon: SameIconActiveWeb,
    },
  ],
  1: [
    {
      icon: LikeIcon,
      title: 'dialog_feedback_satisfied',
      score: 'helpful',
      action: 1,
      activeIcon: LikeIconActiveWeb,
    },
    {
      icon: UnLikeIcon,
      title: 'dialog_feedback_dissatisfied',
      score: 'not_helpful',
      action: 2,
      activeIcon: UnLikeIconActiveWeb,
    },
  ],
};

export const FEEDBACK_CLOSE_STATUS = 7;

export const INPUT_MAX_LENGTH = 500000;

export type RetryConf =
  | {
      retryType?: string;
      replyStyle?: ReplyStyle;
    }
  | undefined
  | null;

export const SOCIAL_TALK = 'socialTalk';
